// /admin — persona review queue.
//
// Renders a login form when there is no admin session, otherwise shows
// the list of pending companions with approve / reject controls and a
// read-only preview of the persona's system prompt + appearance.
//
// Auth model: regular /auth/login flow with the admin's email +
// password. The user row carries `isAdmin: true`; the backend's
// requireAdmin middleware re-checks that flag on every API call.

import React, { useState, useEffect, useCallback } from 'react';
import { login, whoAmI, logout } from '../lib/auth';
import {
  listPending, getCompanionForReview,
  approveCompanion, rejectCompanion,
  listAllForAdmin, adminSetCategory,
  adminUpdateCompanion, adminDeleteCompanion,
  adminAnalyticsSummary,
  adminSearchUsers, adminGetUser, adminGrantCredits, adminResetQuota,
} from '../lib/admin';
import './AdminPage.css';

const ADMIN_CATEGORY_OPTIONS = [
  { value: '',          label: '— Uncategorised —' },
  { value: 'creative',  label: 'Creative' },
  { value: 'knowledge', label: 'Knowledge' },
  { value: 'sports',    label: 'Sports' },
  { value: 'culture',   label: 'Culture' },
  { value: 'learning',  label: 'Learning' },
];

function LoginForm({ onSignedIn }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setBusy(true);
    try {
      const user = await login({ email: email.trim(), password });
      if (!user?.isAdmin) {
        setError('That account is not an admin. Sign out and use the admin email.');
        setBusy(false);
        return;
      }
      onSignedIn(user);
    } catch (err) {
      setError(err.message || 'Login failed.');
      setBusy(false);
    }
  };

  return (
    <div className="admin-login-wrap">
      <form className="admin-login-card" onSubmit={submit}>
        <h1 className="admin-login-title">Admin sign in</h1>
        <p className="admin-login-sub">Persona review console</p>
        <label className="admin-field">
          <span>Email</span>
          <input
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
          />
        </label>
        <label className="admin-field">
          <span>Password</span>
          <input
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </label>
        {error && <div className="admin-error">{error}</div>}
        <button type="submit" className="admin-primary" disabled={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </div>
  );
}

function ReviewItem({ summary, onApproved, onRejected }) {
  const [full, setFull] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  const loadFull = useCallback(async () => {
    setLoading(true);
    try {
      const row = await getCompanionForReview(summary.ownerEmail, summary.companionId);
      setFull(row);
    } catch (err) {
      setError(err.message || 'Failed to load persona.');
    } finally {
      setLoading(false);
    }
  }, [summary.ownerEmail, summary.companionId]);

  useEffect(() => { loadFull(); }, [loadFull]);

  const onApprove = async () => {
    setError(''); setBusy(true);
    try {
      await approveCompanion(summary.ownerEmail, summary.companionId);
      onApproved(summary.companionId);
    } catch (err) {
      setError(err.message || 'Approve failed.');
      setBusy(false);
    }
  };

  const onReject = async () => {
    if (!reason.trim()) {
      setError('Please give the creator a short reason.');
      return;
    }
    setError(''); setBusy(true);
    try {
      await rejectCompanion(summary.ownerEmail, summary.companionId, reason.trim());
      onRejected(summary.companionId);
    } catch (err) {
      setError(err.message || 'Reject failed.');
      setBusy(false);
    }
  };

  return (
    <article className="admin-card">
      <header className="admin-card-head">
        <div className="admin-card-avatar">
          {summary.avatarUrl
            ? <img src={summary.avatarUrl} alt="" />
            : <span>{(summary.name || '?').charAt(0).toUpperCase()}</span>}
        </div>
        <div className="admin-card-meta">
          <h2 className="admin-card-name">{summary.name}</h2>
          <p className="admin-card-tagline">{summary.tagline || '—'}</p>
          <p className="admin-card-owner">
            By <code>{summary.ownerEmail}</code>
            {' · '}
            Submitted {summary.updatedAt
              ? new Date(summary.updatedAt).toLocaleString()
              : 'recently'}
          </p>
        </div>
      </header>

      {loading && <div className="admin-card-loading">Loading persona…</div>}

      {full && (
        <div className="admin-card-body">
          {full.traits?.length > 0 && (
            <div className="admin-section">
              <div className="admin-section-label">Traits</div>
              <div className="admin-chips">
                {full.traits.map((t) => <span key={t} className="admin-chip">{t}</span>)}
              </div>
            </div>
          )}
          {full.description && (
            <div className="admin-section">
              <div className="admin-section-label">Description</div>
              <p className="admin-section-body">{full.description}</p>
            </div>
          )}
          {full.appearance && (
            <div className="admin-section">
              <div className="admin-section-label">Appearance prompt</div>
              <p className="admin-section-body admin-mono">{full.appearance}</p>
            </div>
          )}
          {full.systemPrompt && (
            <details className="admin-section">
              <summary className="admin-section-label">System prompt (the actual model steering) ▾</summary>
              <pre className="admin-section-body admin-mono admin-prompt">{full.systemPrompt}</pre>
            </details>
          )}
        </div>
      )}

      {error && <div className="admin-error">{error}</div>}

      <footer className="admin-card-foot">
        {!showReject ? (
          <>
            <button className="admin-reject" onClick={() => setShowReject(true)} disabled={busy}>
              Reject
            </button>
            <button className="admin-approve" onClick={onApprove} disabled={busy}>
              {busy ? 'Approving…' : 'Approve & publish'}
            </button>
          </>
        ) : (
          <div className="admin-reject-row">
            <input
              type="text"
              placeholder="Reason shown to creator (e.g. 'Too similar to existing preset')"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={400}
              autoFocus
            />
            <button className="admin-cancel" onClick={() => { setShowReject(false); setReason(''); }} disabled={busy}>
              Cancel
            </button>
            <button className="admin-reject" onClick={onReject} disabled={busy}>
              {busy ? 'Rejecting…' : 'Send rejection'}
            </button>
          </div>
        )}
      </footer>
    </article>
  );
}

// Just the queue contents — page chrome (header, tabs) is owned by
// AdminPage so both tabs render under the same shell.
function ReviewQueueBody() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const reload = useCallback(async () => {
    setLoading(true); setError('');
    try {
      setItems(await listPending());
    } catch (err) {
      setError(err.message || 'Failed to load queue.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const removeFromQueue = (id) => {
    setItems((curr) => curr.filter((c) => c.companionId !== id));
  };

  return (
    <div className="admin-queue-body">
      <div className="admin-queue-sub">
        {loading
          ? 'Loading…'
          : `${items.length} pending ${items.length === 1 ? 'submission' : 'submissions'}`}
        <button className="admin-ghost admin-queue-refresh" onClick={reload} disabled={loading}>
          Refresh
        </button>
      </div>

      {error && <div className="admin-error">{error}</div>}

      {!loading && items.length === 0 && (
        <div className="admin-empty">
          <h2>Queue is empty.</h2>
          <p>Nothing pending review right now. Creators submit personas via the “Submit for review” button on their custom Mitras.</p>
        </div>
      )}

      <div className="admin-list">
        {items.map((c) => (
          <ReviewItem
            key={`${c.ownerEmail}::${c.companionId}`}
            summary={c}
            onApproved={removeFromQueue}
            onRejected={removeFromQueue}
          />
        ))}
      </div>
    </div>
  );
}

// Inline editor for one character row in the Manage-All view.
// Surfaces: category dropdown (autosave on change), Edit name/desc
// (modal), Delete with confirm. Edit mirrors the creator's PATCH
// fields but bypasses ownership/visibility rules at the backend.
function ManageRow({ row, onChanged, onRemoved }) {
  const [category, setCategory] = useState(row.category || '');
  const [savingCat, setSavingCat] = useState(false);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(row.name || '');
  const [description, setDescription] = useState(row.description || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const saveCategory = async (next) => {
    setSavingCat(true); setError('');
    try {
      const updated = await adminSetCategory(row.ownerEmail, row.companionId, next || null);
      setCategory(updated?.category || '');
      onChanged?.(updated);
    } catch (err) {
      setError(err.message || 'Could not save category.');
    } finally {
      setSavingCat(false);
    }
  };

  const saveEdit = async () => {
    setBusy(true); setError('');
    try {
      const updated = await adminUpdateCompanion(row.ownerEmail, row.companionId, {
        name: name.trim(),
        description: description.trim(),
      });
      onChanged?.(updated);
      setEditing(false);
    } catch (err) {
      setError(err.message || 'Save failed.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${row.name}" permanently? This cannot be undone.`)) return;
    setBusy(true); setError('');
    try {
      await adminDeleteCompanion(row.ownerEmail, row.companionId);
      onRemoved?.(row);
    } catch (err) {
      setError(err.message || 'Delete failed.');
      setBusy(false);
    }
  };

  return (
    <article className="admin-row">
      <div className="admin-row-avatar">
        {row.avatarUrl
          ? <img src={row.avatarUrl} alt="" />
          : <span>{(row.name || '?').charAt(0).toUpperCase()}</span>}
      </div>
      <div className="admin-row-meta">
        <div className="admin-row-name">{row.name}</div>
        <div className="admin-row-sub">
          {row.tagline || '—'}
        </div>
        <div className="admin-row-attrs">
          <span className={`admin-pill admin-pill-vis-${row.visibility || 'private'}`}>
            {row.visibility || 'private'}
          </span>
          <span className="admin-row-owner"><code>{row.ownerEmail}</code></span>
        </div>
      </div>
      <div className="admin-row-controls">
        <label className="admin-row-cat">
          <span>Category</span>
          <select
            value={category}
            onChange={(e) => { setCategory(e.target.value); saveCategory(e.target.value); }}
            disabled={savingCat || busy}
          >
            {ADMIN_CATEGORY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>
        <div className="admin-row-actions">
          <button className="admin-ghost" onClick={() => setEditing((v) => !v)} disabled={busy}>
            {editing ? 'Cancel' : 'Edit'}
          </button>
          <button className="admin-reject" onClick={remove} disabled={busy}>
            Delete
          </button>
        </div>
      </div>
      {editing && (
        <div className="admin-row-editor">
          <label className="admin-field">
            <span>Name</span>
            <input value={name} onChange={(e) => setName(e.target.value)} maxLength={40} disabled={busy} />
          </label>
          <label className="admin-field">
            <span>Description</span>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              disabled={busy}
            />
          </label>
          <div className="admin-row-editor-actions">
            <button className="admin-ghost" onClick={() => setEditing(false)} disabled={busy}>Cancel</button>
            <button className="admin-approve" onClick={saveEdit} disabled={busy}>
              {busy ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
      )}
      {error && <div className="admin-error">{error}</div>}
    </article>
  );
}

function ManageAll() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all'); // all | public | pending | private | rejected
  const [query, setQuery] = useState('');

  const reload = useCallback(async () => {
    setLoading(true); setError('');
    try {
      setRows(await listAllForAdmin());
    } catch (err) {
      setError(err.message || 'Failed to load characters.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const onChanged = (updated) => {
    if (!updated) return;
    setRows((curr) => curr.map((r) =>
      r.companionId === updated.companionId && r.ownerEmail === updated.ownerEmail
        ? { ...r, ...updated }
        : r
    ));
  };
  const onRemoved = (row) => {
    setRows((curr) => curr.filter((r) =>
      !(r.companionId === row.companionId && r.ownerEmail === row.ownerEmail)
    ));
  };

  const visible = rows.filter((r) => {
    if (filter !== 'all' && (r.visibility || 'private') !== filter) return false;
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      if (!(r.name || '').toLowerCase().includes(q)
          && !(r.ownerEmail || '').toLowerCase().includes(q)) return false;
    }
    return true;
  });

  return (
    <div className="admin-manage">
      <div className="admin-manage-tools">
        <input
          className="admin-search"
          type="search"
          placeholder="Search by name or owner email…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <div className="admin-manage-filters">
          {['all', 'public', 'pending', 'private', 'rejected'].map((k) => (
            <button
              key={k}
              className={`admin-filter ${filter === k ? 'on' : ''}`}
              onClick={() => setFilter(k)}
            >
              {k === 'all' ? 'All' : k.charAt(0).toUpperCase() + k.slice(1)}
            </button>
          ))}
        </div>
        <button className="admin-ghost" onClick={reload} disabled={loading}>Refresh</button>
      </div>
      {error && <div className="admin-error">{error}</div>}
      {loading ? (
        <div className="admin-card-loading">Loading characters…</div>
      ) : visible.length === 0 ? (
        <div className="admin-empty">
          <h2>No characters match.</h2>
          <p>{rows.length === 0
            ? 'There are no characters in the database yet.'
            : 'Try a different filter or clear the search.'}</p>
        </div>
      ) : (
        <div className="admin-manage-list">
          {visible.map((r) => (
            <ManageRow
              key={`${r.ownerEmail}::${r.companionId}`}
              row={r}
              onChanged={onChanged}
              onRemoved={onRemoved}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Analytics dashboard tab ──
// Small stat tiles + a text table showing per-day breakdown. No heavy
// charts (keeps the bundle small); a spark-bar of DAU is enough for
// eyeballing trends and matches the calm design language of the rest
// of the admin console.
function AnalyticsBody() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError('');
    adminAnalyticsSummary({ days })
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError(e.message || 'Failed to load analytics.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days]);

  const totals = data?.totals || {};
  const perDay = data?.perDay || [];
  const dauMax = Math.max(1, ...perDay.map((d) => d.dau || 0));

  return (
    <div className="admin-analytics">
      <div className="admin-analytics-tools">
        <div className="admin-analytics-window">
          <span>Window</span>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} disabled={loading}>
            <option value="7">Last 7 days</option>
            <option value="14">Last 14 days</option>
            <option value="30">Last 30 days</option>
            <option value="60">Last 60 days</option>
            <option value="90">Last 90 days</option>
          </select>
        </div>
      </div>

      {loading && <div className="admin-card-loading">Loading analytics…</div>}
      {error && <div className="admin-error">{error}</div>}

      {data && (
        <>
          <div className="admin-stats">
            <StatTile label="MAU (window)"        value={totals.mau} />
            <StatTile label="Signups"             value={totals.signup} />
            <StatTile label="Signins"             value={totals.signin} />
            <StatTile label="Chat turns"          value={totals.chat_sent} />
            <StatTile label="Companion turns"     value={totals.companion_chat_sent} />
            <StatTile label="Images generated"    value={totals.image_generated} />
            <StatTile label="Characters created"  value={totals.character_created} />
          </div>

          <div className="admin-analytics-table">
            <div className="admin-analytics-table-head">
              <span>Day</span>
              <span>DAU</span>
              <span>Signups</span>
              <span>Chats</span>
              <span>Companion</span>
              <span>Images</span>
            </div>
            {[...perDay].reverse().map((d) => (
              <div key={d.day} className="admin-analytics-row">
                <span className="admin-analytics-day">{d.day}</span>
                <span className="admin-analytics-dau">
                  <span className="admin-analytics-bar">
                    <span
                      className="admin-analytics-bar-fill"
                      style={{ width: `${((d.dau || 0) / dauMax) * 100}%` }}
                    />
                  </span>
                  <span>{d.dau || 0}</span>
                </span>
                <span>{d.counts?.signup || 0}</span>
                <span>{d.counts?.chat_sent || 0}</span>
                <span>{d.counts?.companion_chat_sent || 0}</span>
                <span>{d.counts?.image_generated || 0}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function StatTile({ label, value }) {
  return (
    <div className="admin-stat">
      <div className="admin-stat-value">{Number(value || 0).toLocaleString()}</div>
      <div className="admin-stat-label">{label}</div>
    </div>
  );
}

// ── Users tab ──
// Search by email, load the picked user's usage + credits, grant/deduct
// bonus tokens, or reset the daily/monthly window.
function UsersBody() {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    if (!q.trim() || q.trim().length < 2) { setResults([]); return; }
    const id = setTimeout(async () => {
      try {
        const users = await adminSearchUsers(q.trim());
        if (!cancelled) setResults(users);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Search failed.');
      }
    }, 220);
    return () => { cancelled = true; clearTimeout(id); };
  }, [q]);

  const loadUser = async (email) => {
    setSelectedEmail(email);
    setLoading(true); setError('');
    try {
      setDetail(await adminGetUser(email));
    } catch (err) {
      setError(err.message || 'Failed to load user.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-users">
      <div className="admin-users-search">
        <input
          className="admin-search"
          type="search"
          placeholder="Search by email (min 2 chars)…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      {error && <div className="admin-error">{error}</div>}

      {results.length > 0 && (
        <div className="admin-users-results">
          {results.map((u) => (
            <button
              key={u.email}
              className={`admin-users-result ${u.email === selectedEmail ? 'on' : ''}`}
              onClick={() => loadUser(u.email)}
            >
              <span className="admin-users-email">{u.email}</span>
              <span className="admin-users-meta">
                {u.name || '—'} · {u.plan}
              </span>
            </button>
          ))}
        </div>
      )}

      {loading && <div className="admin-card-loading">Loading user…</div>}

      {!loading && detail && (
        <UserDetail
          data={detail}
          onRefresh={() => loadUser(selectedEmail)}
        />
      )}
    </div>
  );
}

function UserDetail({ data, onRefresh }) {
  const { user, usage, credits, planLimits } = data || {};
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const grant = async (sign) => {
    const n = Number(amount) * sign;
    if (!n) { setError('Enter a token amount.'); return; }
    setBusy(true); setError('');
    try {
      await adminGrantCredits(user.email, { amount: n, reason });
      setAmount(''); setReason('');
      onRefresh();
    } catch (err) {
      setError(err.message || 'Grant failed.');
    } finally {
      setBusy(false);
    }
  };

  // `w` shadows the browser `window` global on purpose — the param is
  // 'day' | 'month' | 'both', and we use `window.confirm` on the global.
  const reset = async (w) => {
    if (!w || !['day', 'month', 'both'].includes(w)) return;
    if (!window.confirm(`Reset ${w === 'both' ? 'daily + monthly' : w} counters for ${user.email}?`)) return;
    setBusy(true); setError('');
    try {
      await adminResetQuota(user.email, w);
      onRefresh();
    } catch (err) {
      setError(err.message || 'Reset failed.');
    } finally {
      setBusy(false);
    }
  };

  const lim = planLimits?.[user?.plan || 'free'] || {};

  return (
    <div className="admin-user-detail">
      <div className="admin-user-head">
        <div>
          <div className="admin-user-name">{user.name || '(no name)'}</div>
          <code className="admin-user-email">{user.email}</code>
        </div>
        <div className="admin-user-badges">
          <span className="admin-pill">{user.plan}</span>
          {user.isAdmin && <span className="admin-pill admin-pill-vis-public">admin</span>}
        </div>
      </div>

      <div className="admin-user-grid">
        <section className="admin-user-card">
          <h3>Usage this cycle</h3>
          <div className="admin-user-row">
            <span>Tokens today</span>
            <strong>{(usage?.tokensToday || 0).toLocaleString()} / {(lim.tokensDay || 0).toLocaleString()}</strong>
          </div>
          <div className="admin-user-row">
            <span>Tokens this month</span>
            <strong>{(usage?.tokensMonth || 0).toLocaleString()} / {(lim.tokensMonth || 0).toLocaleString()}</strong>
          </div>
          <div className="admin-user-row">
            <span>Images today</span>
            <strong>{usage?.imagesToday || 0} / {lim.imagesDay || 0}</strong>
          </div>
          <div className="admin-user-row">
            <span>Images this month</span>
            <strong>{usage?.imagesMonth || 0} / {lim.imagesMonth || 0}</strong>
          </div>
          <div className="admin-user-actions">
            <button className="admin-ghost" onClick={() => reset('day')} disabled={busy}>Reset daily</button>
            <button className="admin-ghost" onClick={() => reset('month')} disabled={busy}>Reset monthly</button>
            <button className="admin-reject" onClick={() => reset('both')} disabled={busy}>Reset both</button>
          </div>
        </section>

        <section className="admin-user-card">
          <h3>Bonus credits</h3>
          <div className="admin-user-row">
            <span>Balance</span>
            <strong>{(credits?.balance || 0).toLocaleString()} tokens</strong>
          </div>
          <div className="admin-user-row">
            <span>Lifetime granted</span>
            <strong>{(credits?.totalGranted || 0).toLocaleString()}</strong>
          </div>
          <div className="admin-user-row">
            <span>Lifetime spent</span>
            <strong>{(credits?.totalSpent || 0).toLocaleString()}</strong>
          </div>
          <div className="admin-user-grant">
            <input
              type="number"
              min="0"
              placeholder="Amount (tokens)"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              disabled={busy}
            />
            <input
              type="text"
              placeholder="Reason (optional)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={200}
              disabled={busy}
            />
            <div className="admin-user-actions">
              <button className="admin-approve" onClick={() => grant(+1)} disabled={busy || !amount}>Grant</button>
              <button className="admin-reject" onClick={() => grant(-1)} disabled={busy || !amount}>Deduct</button>
            </div>
          </div>
          {credits?.history?.length > 0 && (
            <div className="admin-user-history">
              <div className="admin-user-history-head">Recent activity</div>
              {credits.history.slice(0, 6).map((h, i) => (
                <div key={i} className="admin-user-history-row">
                  <span className={h.amount >= 0 ? 'ok' : 'bad'}>
                    {h.amount >= 0 ? '+' : ''}{h.amount.toLocaleString()}
                  </span>
                  <span className="admin-user-history-meta">
                    {new Date(h.t).toLocaleDateString()} · {h.by}
                    {h.reason ? ` · ${h.reason}` : ''}
                  </span>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {error && <div className="admin-error">{error}</div>}
    </div>
  );
}

export default function AdminPage() {
  const [user, setUser] = useState(undefined);   // undefined = checking
  const [tab, setTab] = useState('queue');       // 'queue' | 'all' | 'users' | 'analytics'

  useEffect(() => {
    let cancelled = false;
    whoAmI().then((u) => { if (!cancelled) setUser(u); });
    return () => { cancelled = true; };
  }, []);

  const onSignOut = async () => {
    try { await logout(); } catch {}
    setUser(null);
  };

  if (user === undefined) {
    return <div className="admin-loading">Loading…</div>;
  }

  if (!user || !user.isAdmin) {
    return <LoginForm onSignedIn={setUser} />;
  }

  const subFor = (t) => ({
    queue:     'Approve, reject, or read pending persona submissions.',
    all:       'Manage every character — categorise, edit, or delete.',
    users:     'Search users, grant bonus credits, reset quotas.',
    analytics: 'Signups, DAU, event counts by day.',
  }[t] || '');

  return (
    <div className="admin-page-wrap">
      <div className="admin-page-inner">
        <header className="admin-page-head">
          <div>
            <h1 className="admin-page-title">Admin console</h1>
            <p className="admin-page-sub">{subFor(tab)}</p>
          </div>
          <div className="admin-page-actions">
            <div className="admin-user-chip" title={user.email}>{user.email}</div>
            <button className="admin-ghost" onClick={onSignOut}>Sign out</button>
          </div>
        </header>
        <div className="admin-tabs" role="tablist">
          {[
            { id: 'queue',     label: 'Review queue' },
            { id: 'all',       label: 'All characters' },
            { id: 'users',     label: 'Users' },
            { id: 'analytics', label: 'Analytics' },
          ].map((t) => (
            <button
              key={t.id}
              role="tab"
              aria-selected={tab === t.id}
              className={`admin-tab ${tab === t.id ? 'on' : ''}`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
        {tab === 'queue'     && <ReviewQueueBody />}
        {tab === 'all'       && <ManageAll />}
        {tab === 'users'     && <UsersBody />}
        {tab === 'analytics' && <AnalyticsBody />}
      </div>
    </div>
  );
}
